import type { CancelBookingFormValues } from '@/scopes/account/booking-details/pages/components/CancelBookingAction/types'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useBookingDetailsStore } from '@/scopes/account/booking-details/infrastructure/useBookingDetailsStore'
import { getActiveParticipantOptions } from '@/scopes/account/booking-details/pages/selectors'
import { formatParticipantFullName } from '@/scopes/account/booking-details/pages/utils/participantName'
import { CheckboxGroupInput } from '@/scopes/common/components/FormElements/CheckboxGroupInput'
import styles from './PartialCancellationStep.module.css'

export function PartialCancellationStep() {
  const { formatMessage, formatMessageIfExists } = useIntl()
  const { formState, getFieldState } = useFormContext<CancelBookingFormValues>()
  const participants = useBookingDetailsStore(getActiveParticipantOptions)

  const flexibleFlightHeading = formatMessageIfExists({ id: 'booking_details_cancel_booking_partial_cancellation_flexible_flight_heading' })
  const flexibleFlightText = formatMessageIfExists({ id: 'booking_details_cancel_booking_partial_cancellation_flexible_flight_text' })

  const participantIdsFieldState = getFieldState('participantIds', formState)

  return (
    <>
      <div className={styles.infoParagraph}>
        <Typography variant="body-m-bold-snug" as="p">
          {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_heading' })}
        </Typography>
        <Typography variant="body-m-regular-snug" as="p">
          {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_text' })}
        </Typography>
      </div>

      <FormField
        label={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_participants_label' })}
        status={participantIdsFieldState.invalid ? 'error' : undefined}
        feedbackText={participantIdsFieldState.error?.message}
      >
        <div className={styles.participantsList}>
          {participants.map(({ value: participantId, fullName: { titleKey, firstName, lastName } }, index) => (
            <CheckboxGroupInput
              key={participantId}
              name="participantIds"
              value={participantId}
              variant="outlined"
              hideErrorMessage
              rules={index === 0
                ? {
                    validate: value => value.length > 0 || formatMessage({
                      id: 'booking_details_cancel_booking_partial_cancellation_participants_required_error',
                    }),
                  }
                : undefined}
              label={formatParticipantFullName(
                titleKey ? formatMessage({ id: titleKey }) : null,
                firstName,
                lastName,
              )}
            />
          ))}
        </div>
      </FormField>
      <div className={styles.infoParagraphs}>
        <div className={styles.infoParagraph}>
          <Typography variant="body-m-bold-snug" as="p">
            {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_external_flight_heading' })}
          </Typography>
          <Typography variant="body-m-regular-snug" as="p">
            {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_external_flight_text' })}
          </Typography>
        </div>
        {flexibleFlightHeading && flexibleFlightText && (
          <div className={styles.infoParagraph}>
            <Typography variant="body-m-bold-snug" as="p">
              {flexibleFlightHeading}
            </Typography>
            <Typography variant="body-m-regular-snug" as="p">
              {flexibleFlightText}
            </Typography>
          </div>
        )}
      </div>
    </>
  )
}
